import { HeaderMenuPanel } from '@/components/content/Header/parts/HeaderMenuPanel';
import { LocateStoreGoBackButton } from '@/components/content/Header/parts/LocateStore/parts/GoBackButton';
import { LocateStorePanelTitle } from '@/components/content/Header/parts/LocateStore/parts/PanelTitle';
import { onLocateStoreOpen } from '@/components/content/Header/parts/LocateStore/utils/locateStoreEvents';
import { LocationAlert } from '@/components/content/Header/parts/LocationAlert';
import { useLocationAlert } from '@/components/content/Header/parts/LocationAlert/hooks/useLocationAlert';
import { HeaderUseMyLocationDialog } from '@/components/content/Header/parts/UseMyLocationDialog';
import { headerLocateStoreSX } from '@/components/content/Header/styles/locateStore/locateStore';
import { StoreLocatorHeaderInfo } from '@/components/content/StoreLocator/components/StoreLocatorHeaderInfo/StoreLocatorHeaderInfo';
import { StoreLocatorStateDropdown } from '@/components/content/StoreLocator/components/StoreLocatorStateDropdown/StoreLocatorStateDropdown';
import { geoLocalizationInit } from '@/components/content/StoreLocator/utils/storeLocatorUtils';
import { useStoreLocator } from '@/data/Content/StoreLocator';
import stateCitiesData from '@/data/Content/StoreLocator/storeLocatorStateCitiesList.json';
import { ContentProvider } from '@/data/context/content';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { useStoreSwitcher } from '@/data/utils/useStoreSwitcher';
import { beginAutoGeolocation, endAutoGeolocation } from '@/utils/locationAlertEvents';
import { findStoreDataByStLocId } from '@/utils/storeLocatorUtils';
import { useCurrentPhysicalStoreId } from '@/utils/useCurrentPhysicalStoreId';
import { useGeolocateStore } from '@/utils/useGeolocateStore';
import { useGeolocationStatus } from '@/utils/useGeolocationStatus';
import { Box, Divider, Stack } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	DEFAULT_STATE_ID,
	DEFAULT_STORE_ID,
	GEO_LOCATION_PERMISSION_PROMPT,
} from 'shared/custom/constants/commonConstants';
import { StoreData } from 'shared/custom/types/commonTypes';
import { getStoreIdAndStateId } from 'shared/custom/utils/cookieHandler';

export type CurrentStoreType = {
	stateId: string;
	stLocId: string;
};

export const HeaderLocateStore: FC = () => {
	const [isLocatorOptionSelected, setIsLocatorOptionSelected] = useState<boolean>(false);
	const [openGeolocalizationRequestPanel, setOpenGeolocalizationRequestPanel] = useState(false);
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const {
		enableGeolocButton,
		isLoadingGeoLocResponse,
		validateGeolocationStatus,
		setEnableGeolocButton,
	} = useGeolocationStatus();
	const [geoState, setGeoState] = useState<string>('');
	const [currentPhysicalStoreValue, setCurrentPhysicalStoreValue] = useState<string>('');
	const [currentStoreValues, setCurrentStoreValues] = useState<CurrentStoreType>({
		stateId: '',
		stLocId: '',
	});
	const [isSettingLocatorDefault, setIsSettingLocatorDefault] = useState<boolean>(false);
	const { storeLocator } = useStoreLocatorState();
	const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
	const stackRef = useRef<HTMLDivElement>(null);
	const isOpenAnchor = Boolean(anchorEl);
	const { isVisible: isLocationAlertVisible, dismiss: dismissLocationAlert } =
		useLocationAlert('store');

	useEffect(() => {
		if (isOpenAnchor) dismissLocationAlert();
	}, [isOpenAnchor, dismissLocationAlert]);

	const allStoresData = useMemo(
		() => stateCitiesData.formScheduleObject.flatMap((s) => s.stores),
		[]
	);
	const { switchStore } = useStoreSwitcher(allStoresData);

	const setStoreValues = useCallback((stateId: string, stLocId: string) => {
		setGeoState(stateId);
		setCurrentPhysicalStoreValue(stLocId);
		setCurrentStoreValues({ stateId, stLocId });
	}, []);

	const getStoreValuesById = useCallback(
		(stLocId: string): CurrentStoreType => ({
			stateId: findStoreDataByStLocId(stLocId)?.stateId ?? '',
			stLocId,
		}),
		[]
	);

	const syncStoreValues = useCallback(
		(stLocId: string) => {
			const values = getStoreValuesById(stLocId);
			setStoreValues(values.stateId, values.stLocId);
			return values;
		},
		[getStoreValuesById, setStoreValues]
	);
	const handleOpen = useCallback(() => {
		setAnchorEl(stackRef.current);
		setIsLocatorOptionSelected(true);
		setGeoState(currentStoreValues.stateId);
	}, [currentStoreValues.stateId]);

	const handleStoreSelected = useCallback(
		async (storeData: StoreData) => {
			const stateFullData = findStoreDataByStLocId(storeData.stLocId);
			await switchStore(
				stateFullData?.stateId ?? '',
				storeData.stLocId,
				storeData.marketId ?? '',
				true
			);
		},
		[switchStore]
	);
	const handleClose = () => {
		setOpenDialog(false);
		setOpenGeolocalizationRequestPanel(false);
		setIsLocatorOptionSelected(false);
		setAnchorEl(null);
	};

	useEffect(() => onLocateStoreOpen(handleOpen), [handleOpen]);

	const {
		data,
		locator,
		setLocator,
		setCurrentLocation,
		searchLatLng,
		showDirection,
		destination,
		mapCenter,
		directionResults,
		searchTextFieldRef,
		currentLocation,
		clickedIndex,
		searchTerm,
		clearSearch,
		mapOnLoad,
		directionsCallback,
		findNearStore,
		onPlaceChanged,
		searchBoxOnLoad,
		onListItemClick,
		...rest
	} = useStoreLocator();

	const setDefaultStore = useCallback(
		async (store_code: string) => {
			setIsSettingLocatorDefault(true);

			try {
				const stateFullData = findStoreDataByStLocId(store_code);
				if (stateFullData) {
					const store = stateFullData.stores?.find((s) => s.stLocId === store_code);
					await switchStore(stateFullData.stateId ?? '', store_code, store?.marketId ?? '', true);
					return;
				}

				const geoLocationCookie = getStoreIdAndStateId();
				if (geoLocationCookie) {
					await switchStore(geoLocationCookie.stateId, geoLocationCookie.stLocId, '', true);
					return;
				}

				await switchStore(DEFAULT_STATE_ID, DEFAULT_STORE_ID, '21', true);
			} finally {
				setIsSettingLocatorDefault(false);
			}
		},
		[switchStore]
	);
	useEffect(() => {
		if (storeLocator.selectedStore && storeLocator.selectedStore?.isExternal) {
			syncStoreValues(storeLocator.selectedStore.id);
		}
	}, [storeLocator.selectedStore, syncStoreValues]);

	// geoLocationHDP cookie is shared across tabs, so this keeps the header in sync
	// when the store is changed from another tab
	const { physicalStoreId: cookieStoreId } = useCurrentPhysicalStoreId();
	useEffect(() => {
		if (cookieStoreId) syncStoreValues(cookieStoreId);
	}, [cookieStoreId, syncStoreValues]);

	const {
		isLoadingGeolocalization,
		handleAcceptGeolocalization,
		handleCancelGeolocalization,
		saveGeolocation,
	} = useGeolocateStore({
		onStoreFound: ({ stateId, stLocId }) => {
			setStoreValues(stateId, stLocId);
			handleClose();
		},
		onCancel: () => {
			const geoLocationCookie = getStoreIdAndStateId();
			setOpenGeolocalizationRequestPanel(false);
			setOpenDialog(false);
			setStoreValues(geoLocationCookie.stateId, geoLocationCookie.stLocId);
		},
		onError: (error) => {
			handleOpen();
			if (error.code === error.PERMISSION_DENIED) {
				validateGeolocationStatus();
			}
		},
	});

	// Automatic browser dialog response
	const triggerBrowserGeolocation = () => {
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				try {
					await saveGeolocation(position, { isAutomatic: true });
				} finally {
					endAutoGeolocation();
				}
			},
			(err) => {
				if (err.code === err.PERMISSION_DENIED) {
					validateGeolocationStatus();
					setOpenDialog(true);
				}
				endAutoGeolocation();
			}
		);
	};
	useEffect(() => {
		// The location alert waits until the store and the delivery zip code are final
		beginAutoGeolocation();
		const params = new URLSearchParams(window.location.search);
		const store_code = params.get('store_code');
		const initGeolocation = (permission: string) => {
			let isGeolocationTriggered = false;
			geoLocalizationInit(permission, setStoreValues, setEnableGeolocButton, () => {
				isGeolocationTriggered = true;
				triggerBrowserGeolocation();
			});
			// Without the automatic geolocation the store and zip code are already final
			if (!isGeolocationTriggered) endAutoGeolocation();
		};
		if (store_code) {
			setDefaultStore(store_code).finally(endAutoGeolocation);
		} else if (navigator?.permissions) {
			navigator.permissions
				.query({ name: 'geolocation' })
				.then((result) => initGeolocation(result.state))
				.catch(endAutoGeolocation);
		} else {
			initGeolocation(GEO_LOCATION_PERMISSION_PROMPT);
		}
		// eslint-disable-next-line
	}, []);

	const handleOpenStoreSelect = () => {
		handleOpen();
		handleCancelGeolocalization();
	};

	const GeolocalizationRequestPanel = (
		<>
			<LocateStoreGoBackButton handleClick={handleOpenStoreSelect} />
			<HeaderUseMyLocationDialog
				openDialog={openDialog && !isLoadingGeoLocResponse}
				loading={isLoadingGeolocalization}
				mobileBreakpoint="md"
				onConfirm={handleAcceptGeolocalization}
				onStoreSelect={handleOpenStoreSelect}
				isDialog={false}
				enableGeolocButton={enableGeolocButton}
			/>
		</>
	);

	const children = (
		<>
			<LocateStorePanelTitle handleClose={handleClose} />
			<Divider />
			<Box sx={headerLocateStoreSX.container}>
				{openGeolocalizationRequestPanel ? (
					GeolocalizationRequestPanel
				) : (
					<StoreLocatorStateDropdown
						closeDrawer={handleClose}
						setOpenGeolocalizationRequestPanel={setOpenGeolocalizationRequestPanel}
						geoState={geoState}
						setGeoState={setGeoState}
						setCurrentStoreValues={setCurrentStoreValues}
						currentPhysicalStoreValue={currentPhysicalStoreValue}
						setCurrentPhysicalStoreValue={setCurrentPhysicalStoreValue}
						onStoreSelected={handleStoreSelected}
						enableGeolocButton={enableGeolocButton}
						GoBackButton={LocateStoreGoBackButton}
					/>
				)}
			</Box>
		</>
	);

	return (
		<ContentProvider
			value={{
				locator,
				showDirection,
				mapCenter,
				clickedIndex,
				searchTerm,
				searchTextFieldRef,
				currentLocation,
				findNearStore,
				searchBoxOnLoad,
				onPlaceChanged,
				onListItemClick,
				clearSearch,
				...rest,
			}}
		>
			<Stack ref={stackRef} onClick={handleOpen}>
				<StoreLocatorHeaderInfo
					currentStoreValues={currentStoreValues}
					isLocatorOptionSelected={isLocatorOptionSelected}
					isSettingLocatorDefault={isSettingLocatorDefault}
				/>
			</Stack>
			<LocationAlert
				variant="store"
				anchorEl={stackRef.current}
				isVisible={isLocationAlertVisible}
				onAction={handleOpen}
				onDismiss={dismissLocationAlert}
			/>
			<HeaderMenuPanel isOpen={isOpenAnchor} handleClose={handleClose} anchorEl={anchorEl}>
				{children}
			</HeaderMenuPanel>
			<HeaderUseMyLocationDialog
				openDialog={openDialog && !isLoadingGeoLocResponse}
				loading={isLoadingGeolocalization}
				mobileBreakpoint="md"
				enableGeolocButton={enableGeolocButton}
				onConfirm={handleAcceptGeolocalization}
				onStoreSelect={handleOpenStoreSelect}
				onClose={handleCancelGeolocalization}
				isDialog
			/>
		</ContentProvider>
	);
};
